/* eslint-disable no-param-reassign */
import { Draft } from 'immer';
import { CalendarDate, getLocalTimeZone, parseDate, today } from '@internationalized/date';

import { types } from '../../../../web-ui-jobs/src/ducks/filterBar';
import type { FilterBarState, RangeValue } from './types';
import { OwnerFilterItem, OwnerType } from './types';
import { getInitialState, OWNER_TYPES, pageDefault, VISUAL_SLICE_NAME } from './constants';

export const { toRelativeDateRange, toCalendarDate, isRelativeDateRange } = types;

export type SavedVisualFilters = {
  start?: string | null,
  end?: string | null,
  ownerId?: string | null,
  ownerType?: string | null,
  buildName?: string | null,
  status?: string[] | null,
  project?: string | null,
  branch?: string | null,
  page?: number | null,
}

export const serializeFilters = (state: FilterBarState) => {
  const savedFilterState = {
    start: state.date.start ?? '',
    end: state.date.end ?? '',
    ownerId: state.owner.value ?? '',
    ownerType: state.owner.type ?? '',
    buildName: state.buildName.value ?? '',
    status: state.status.value,
    project: state.project.value ?? '',
    branch: state.branch.value ?? '',
    page: state.page.value || pageDefault,
  };

  const nonEmptyEntries = Object
    .entries(savedFilterState)
    .filter(([, value]) => (
      Array.isArray(value) ? value.length > 0 : !!value
    ));

  return Object.fromEntries(nonEmptyEntries);
};

export const parseSavedFilters = (search: string) => {
  const params = new URLSearchParams(search);

  let savedSettings: SavedVisualFilters = {};

  // Should be replaced with params.size when we can upgrade TypeScript
  if ([...params].length > 0) {
    savedSettings = {
      start: params.get('start'),
      end: params.get('end'),
      ownerId: params.get('ownerId'),
      ownerType: params.get('ownerType'),
      buildName: params.get('buildName'),
      status: params.get('status')?.split(','),
      project: params.get('project'),
      branch: params.get('branch'),
      page: Number(params.get('page')) || pageDefault,
    };
  } else {
    try {
      savedSettings = JSON.parse(localStorage.getItem(VISUAL_SLICE_NAME) ?? '{}');
    } catch {
      savedSettings = {};
    }
  }

  return savedSettings;
};

export const isOwnerType = (value: unknown): value is OwnerType => (
  OWNER_TYPES.includes(<OwnerType>value)
);

export const setUserValue = (state: Draft<FilterBarState>, owner: OwnerFilterItem) => {
  state.owner.value = owner.key;
  state.owner.type = owner.type;
  state.owner.label = owner.label;
};

export const setDefaultUser = (state: Draft<FilterBarState>) => {
  const myTestUser = state.owner.items.find(
    (user) => user.label.indexOf('My tests') === 0,
  );

  if (myTestUser) {
    setUserValue(state, myTestUser);
  } else {
    state.owner = getInitialState().owner;
  }
};

/**
 * Parses and validate date strings from user search / url params.
 * @param start
 * @param end
 * @throws Error
 */
export const parseAndValidateDates = (
  { start, end }: {start: string, end: string},
): RangeValue<CalendarDate> => {
  const maxRange: RangeValue<CalendarDate> = {
    start: today(getLocalTimeZone()).subtract({ days: 89 }),
    end: today(getLocalTimeZone()),
  };

  let startDate: CalendarDate = maxRange.start;
  let endDate: CalendarDate = maxRange.end;

  if (start) {
    startDate = parseDate(start);
  }

  if (end) {
    endDate = parseDate(end);
  }

  return {
    start: startDate.compare(maxRange.start) > 0 ? startDate : maxRange.start,
    end: endDate.compare(maxRange.end) < 0 ? endDate : maxRange.end,
  };
};

/**
 * Converts a string into a query suitable for FullText search in Postgres based
 * on how a user would typically search for information.
 *
 * @param value
 */
export const convertStringToFullTextSearch = (value: string) => (
  value
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((textItem) => (/\*$/.test(textItem) ? textItem : `${textItem}*`))
    .join(' ')
);
